import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer,
  CartesianGrid, LineChart, Line, PieChart, Pie, Cell
} from 'recharts';

// Chart palette tied to the design system. Status colors come straight
// from the warn/bad/good tokens so the charts feel native to the rest
// of the app. Stacked-segment colors are tuned for readability when
// piled on the same bar.
const CHART_COLORS = {
  email:   '#0e7490',  // accent (teal)
  quote:   '#9333ea',  // purple
  bulloch: '#ea580c',  // orange
  phone:   '#0ea5e9',
  meeting: '#16a34a',
  demo:    '#9333ea',
  other:   '#6b7280',
  opened:  '#16a34a',
  bad:     '#b91c1c',
  warn:    '#b45309',
  good:    '#047857',
  dim:     '#6b7280',
  // outreach cadence
  initial: '#facc15', followup: '#fb923c', breakup: '#ef4444',
  closed:  '#16a34a', won:      '#16a34a'
};

// Reports page (Dave 2026-06-12) -- starting with one report,
// Emails Sent by User. Designed so we can keep adding cards as the
// list of useful aggregates grows (lead conversions, quote close rate,
// outreach cadence, churn). Each report is its own self-contained
// component with its own data fetch so a slow report doesn't block
// the rest of the page.

// Shared date-range options. Names + ids match the Email Tracking
// page so the same vocabulary applies. The 'all' option intentionally
// has no from/to so the server returns lifetime totals.
const DATE_RANGES = [
  { id: 'all',         label: 'All time' },
  { id: '7d',          label: 'Last 7 days' },
  { id: '30d',         label: 'Last 30 days' },
  { id: 'this_month',  label: 'This month' },
  { id: 'last_month',  label: 'Last month' },
  { id: 'ytd',         label: 'Year to date' }
];

// Compute concrete from/to dates for a range id. Returns
// { from, to } with ISO date strings; either can be null for unbounded.
function dateRange(id) {
  const today = new Date();
  const iso = (d) => d.toISOString().slice(0, 10);
  if (id === 'all') return { from: null, to: null };
  if (id === '7d') {
    const from = new Date(today); from.setDate(from.getDate() - 6);
    return { from: iso(from), to: iso(today) };
  }
  if (id === '30d') {
    const from = new Date(today); from.setDate(from.getDate() - 29);
    return { from: iso(from), to: iso(today) };
  }
  if (id === 'this_month') {
    const from = new Date(today.getFullYear(), today.getMonth(), 1);
    return { from: iso(from), to: iso(today) };
  }
  if (id === 'last_month') {
    const from = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const to   = new Date(today.getFullYear(), today.getMonth(), 0);
    return { from: iso(from), to: iso(to) };
  }
  if (id === 'ytd') {
    const from = new Date(today.getFullYear(), 0, 1);
    return { from: iso(from), to: iso(today) };
  }
  return { from: null, to: null };
}

export default function Reports() {
  // Single shared date-range selector at the top -- each report card
  // listens to it so changing the range refreshes them all. Default
  // 'all' since Dave's first ask was a lifetime total ("how many
  // emails has Kaden sent").
  const [rangeId, setRangeId] = useState('all');
  const range = useMemo(() => dateRange(rangeId), [rangeId]);

  // Page-wide display mode (Dave 2026-06-12). 'table' shows everything
  // as data tables (the default -- easier to scan exact numbers).
  // 'chart' swaps every card to a visual. Persists per-browser so the
  // preference sticks across sessions.
  const DISPLAY_KEY = 'reports.display';
  const [display, setDisplay] = useState(() => {
    try { return localStorage.getItem(DISPLAY_KEY) || 'table'; } catch (_) { return 'table'; }
  });
  useEffect(() => {
    try { localStorage.setItem(DISPLAY_KEY, display); } catch (_) { /* noop */ }
  }, [display]);

  return (
    <section className="page">
      <header className="page-head">
        <h2>Reports</h2>
        <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Display: Table | Chart segmented control. Affects every
              card on the page (single source of truth). */}
          <div style={{
            display: 'inline-flex',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-md)',
            overflow: 'hidden',
            fontSize: 'var(--text-small)'
          }}>
            {[
              { id: 'table', label: 'Table' },
              { id: 'chart', label: 'Chart' }
            ].map((opt) => {
              const isActive = display === opt.id;
              return (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setDisplay(opt.id)}
                  style={{
                    padding: '6px 14px',
                    background: isActive ? 'var(--color-accent)' : 'var(--color-surface)',
                    color:      isActive ? '#fff'                : 'var(--color-text)',
                    border: 'none',
                    cursor: 'pointer',
                    fontWeight: isActive ? 'var(--font-semibold)' : 'var(--font-medium)'
                  }}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
          <label style={{ fontSize: 'var(--text-small)', color: 'var(--color-text-dim)' }}>
            Date range:
          </label>
          <select
            value={rangeId}
            onChange={(e) => setRangeId(e.target.value)}
            style={{ height: 'var(--input-h-md)' }}
          >
            {DATE_RANGES.map((r) => (
              <option key={r.id} value={r.id}>{r.label}</option>
            ))}
          </select>
        </div>
      </header>

      <EmailsByUserCard           range={range} display={display} />
      <NotesByUserCard            range={range} display={display} />
      <TicketsByUserCard          range={range} display={display} />
      <NewCustomersByMonthCard    range={range} display={display} />
      <OutreachCadenceCard                     display={display} />
      <SiteChurnByMonthCard       range={range} display={display} />
      <SftpCoverageCard                          display={display} />
    </section>
  );
}

// Generic empty-state card chrome.
function ReportCard({ title, subtitle, headerRight, loading, error, empty, children }) {
  return (
    <div className="card" style={{ marginTop: 'var(--space-4)', padding: 'var(--card-pad)' }}>
      <header style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 'var(--space-3)',
        marginBottom: 'var(--space-3)',
        flexWrap: 'wrap'
      }}>
        <div>
          <h3 style={{ margin: 0 }}>{title}</h3>
          {subtitle && (
            <p style={{ margin: '4px 0 0', color: 'var(--color-text-dim)', fontSize: 'var(--text-small)' }}>
              {subtitle}
            </p>
          )}
        </div>
        {headerRight}
      </header>
      {loading && <p>Loading…</p>}
      {error && <p className="error">Error: {error}</p>}
      {!loading && !error && empty && <p className="panel-empty">{empty}</p>}
      {!loading && !error && !empty && children}
    </div>
  );
}

// Helper to fetch a report and expose {rows, loading, error}. The
// fetch re-runs whenever `params` changes by JSON identity.
function useReport(path, params) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const key = JSON.stringify(params || {});
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    axios.get(path, { params })
      .then((r) => { if (!cancelled) setRows(r.data.rows || []); })
      .catch((e) => { if (!cancelled) setError(e.response?.data?.error || e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, key]);
  return { rows, loading, error };
}

// Notes Logged by User -- per-user counts split by note type (Phone,
// Email, Meeting, Demo, Other). Dave's "how many phone calls" question.
function NotesByUserCard({ range, display = 'table' }) {
  const { rows, loading, error } = useReport('/api/reports/notes-by-user', {
    from: range.from || undefined,
    to:   range.to   || undefined
  });
  return (
    <ReportCard
      title="Notes Logged by User"
      subtitle="Calls, emails, meetings, demos, and other notes captured per user."
      loading={loading}
      error={error}
      empty={rows && rows.length === 0 ? 'No notes in this range.' : null}
    >
      {rows && rows.length > 0 && display === 'chart' && (
        <ResponsiveContainer width="100%" height={Math.max(120, rows.length * 50)}>
          <BarChart data={rows} layout="vertical" margin={{ top: 8, right: 24, bottom: 8, left: 24 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis type="number" stroke="var(--color-text-dim)" />
            <YAxis type="category" dataKey="user_name" stroke="var(--color-text-dim)" width={140} />
            <Tooltip />
            <Legend />
            <Bar dataKey="phone"   name="Phone"   stackId="a" fill={CHART_COLORS.phone} />
            <Bar dataKey="email"   name="Email"   stackId="a" fill={CHART_COLORS.email} />
            <Bar dataKey="meeting" name="Meeting" stackId="a" fill={CHART_COLORS.meeting} />
            <Bar dataKey="demo"    name="Demo"    stackId="a" fill={CHART_COLORS.demo} />
            <Bar dataKey="other"   name="Other"   stackId="a" fill={CHART_COLORS.other} />
          </BarChart>
        </ResponsiveContainer>
      )}
      {rows && rows.length > 0 && display === 'table' && (
        <table className="data-table" style={{ width: '100%' }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}>User</th>
              <th style={{ textAlign: 'right' }}>Total</th>
              <th style={{ textAlign: 'right' }}>Phone</th>
              <th style={{ textAlign: 'right' }}>Email</th>
              <th style={{ textAlign: 'right' }}>Meeting</th>
              <th style={{ textAlign: 'right' }}>Demo</th>
              <th style={{ textAlign: 'right' }}>Other</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.user_email || r.user_name}>
                <td>
                  <div style={{ fontWeight: 'var(--font-semibold)' }}>{r.user_name}</div>
                  {r.user_email && (
                    <div className="cell-mono" style={{ color: 'var(--color-text-dim)', fontSize: 'var(--text-small)' }}>
                      {r.user_email}
                    </div>
                  )}
                </td>
                <td className="cell-mono" style={{ textAlign: 'right', fontWeight: 'var(--font-semibold)' }}>{r.total.toLocaleString()}</td>
                <td className="cell-mono" style={{ textAlign: 'right' }}>{r.phone.toLocaleString()}</td>
                <td className="cell-mono" style={{ textAlign: 'right' }}>{r.email.toLocaleString()}</td>
                <td className="cell-mono" style={{ textAlign: 'right' }}>{r.meeting.toLocaleString()}</td>
                <td className="cell-mono" style={{ textAlign: 'right' }}>{r.demo.toLocaleString()}</td>
                <td className="cell-mono" style={{ textAlign: 'right' }}>{r.other.toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </ReportCard>
  );
}

// Tickets by User -- opened, closed, and currently open assigned.
function TicketsByUserCard({ range, display = 'table' }) {
  const { rows, loading, error } = useReport('/api/reports/tickets-by-user', {
    from: range.from || undefined,
    to:   range.to   || undefined
  });
  return (
    <ReportCard
      title="Tickets by User"
      subtitle="Opened by + closed by + currently assigned. Closed count is anchored on closed_at, opens on created_at."
      loading={loading}
      error={error}
      empty={rows && rows.length === 0 ? 'No ticket activity in this range.' : null}
    >
      {rows && rows.length > 0 && display === 'chart' && (
        <ResponsiveContainer width="100%" height={Math.max(120, rows.length * 50)}>
          <BarChart data={rows} layout="vertical" margin={{ top: 8, right: 24, bottom: 8, left: 24 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis type="number" stroke="var(--color-text-dim)" />
            <YAxis type="category" dataKey="user_name" stroke="var(--color-text-dim)" width={140} />
            <Tooltip />
            <Legend />
            <Bar dataKey="opened"   name="Opened"   fill={CHART_COLORS.dim} />
            <Bar dataKey="closed"   name="Closed"   fill={CHART_COLORS.good} />
            <Bar dataKey="open_now" name="Open Now" fill={CHART_COLORS.warn} />
          </BarChart>
        </ResponsiveContainer>
      )}
      {rows && rows.length > 0 && display === 'table' && (
        <table className="data-table" style={{ width: '100%' }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}>User</th>
              <th style={{ textAlign: 'right' }}>Opened</th>
              <th style={{ textAlign: 'right' }}>Closed</th>
              <th style={{ textAlign: 'right' }}>Open Now</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.user_email || r.user_name}>
                <td>
                  <div style={{ fontWeight: 'var(--font-semibold)' }}>{r.user_name}</div>
                  {r.user_email && (
                    <div className="cell-mono" style={{ color: 'var(--color-text-dim)', fontSize: 'var(--text-small)' }}>
                      {r.user_email}
                    </div>
                  )}
                </td>
                <td className="cell-mono" style={{ textAlign: 'right' }}>{r.opened.toLocaleString()}</td>
                <td className="cell-mono" style={{ textAlign: 'right' }}>{r.closed.toLocaleString()}</td>
                <td className="cell-mono" style={{ textAlign: 'right', color: r.open_now > 0 ? 'var(--color-warn)' : 'var(--color-text-dim)' }}>
                  {r.open_now.toLocaleString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </ReportCard>
  );
}

// New Customers by Month -- count of new customer contacts per month.
function NewCustomersByMonthCard({ range, display = 'table' }) {
  const { rows, loading, error } = useReport('/api/reports/new-customers-by-month', {
    from: range.from || undefined,
    to:   range.to   || undefined
  });
  // Chart wants oldest-first so the timeline reads left-to-right.
  const chartRows = useMemo(() => (rows ? [...rows].reverse() : []), [rows]);
  return (
    <ReportCard
      title="New Customer Sign-ups by Month"
      subtitle="Customers created in Command per calendar month (most recent first)."
      loading={loading}
      error={error}
      empty={rows && rows.length === 0 ? 'No new customers in this range.' : null}
    >
      {rows && rows.length > 0 && display === 'chart' && (
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={chartRows} margin={{ top: 8, right: 24, bottom: 8, left: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis dataKey="month" stroke="var(--color-text-dim)" />
            <YAxis allowDecimals={false} stroke="var(--color-text-dim)" />
            <Tooltip />
            <Bar dataKey="customers" name="New customers" fill={CHART_COLORS.email} />
          </BarChart>
        </ResponsiveContainer>
      )}
      {rows && rows.length > 0 && display === 'table' && (
        <table className="data-table" style={{ width: '100%' }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}>Month</th>
              <th style={{ textAlign: 'right' }}>New Customers</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.month}>
                <td className="cell-mono">{r.month}</td>
                <td className="cell-mono" style={{ textAlign: 'right', fontWeight: 'var(--font-semibold)' }}>
                  {r.customers.toLocaleString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </ReportCard>
  );
}

// Outreach Cadence Snapshot -- right-now leads grouped by stage.
function OutreachCadenceCard({ display = 'table' }) {
  const { rows, loading, error } = useReport('/api/reports/outreach-cadence', null);
  // Color tokens matching the dashboard stage dots.
  const STAGE_TONE = {
    initial:  '#facc15', // yellow
    followup: '#fb923c', // orange
    breakup:  '#ef4444', // red
    closed:   '#16a34a', // green
    won:      '#16a34a'
  };
  return (
    <ReportCard
      title="Lead Outreach Cadence (Current Snapshot)"
      subtitle="Current count of leads at each outreach stage. Updates in real time -- not date-filtered."
      loading={loading}
      error={error}
      empty={rows && rows.length === 0 ? 'No leads with outreach stages set.' : null}
    >
      {rows && rows.length > 0 && display === 'chart' && (
        <ResponsiveContainer width="100%" height={280}>
          <PieChart>
            <Pie
              data={rows}
              dataKey="total"
              nameKey="stage"
              cx="50%"
              cy="50%"
              outerRadius={100}
              label={(d) => `${d.stage} (${d.total})`}
            >
              {rows.map((r) => (
                <Cell key={r.stage} fill={STAGE_TONE[r.stage] || CHART_COLORS.dim} />
              ))}
            </Pie>
            <Tooltip />
          </PieChart>
        </ResponsiveContainer>
      )}
      {rows && rows.length > 0 && display === 'table' && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-3)' }}>
          {rows.map((r) => (
            <div key={r.stage} style={{
              padding: 'var(--space-3) var(--space-4)',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--color-border)',
              background: 'var(--color-surface)',
              minWidth: 140
            }}>
              <div style={{ fontSize: 'var(--text-caption)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-wider)', color: 'var(--color-text-dim)' }}>
                {r.stage}
              </div>
              <div style={{
                fontSize: 'var(--text-title)',
                fontFamily: 'var(--font-mono)',
                fontWeight: 'var(--font-bold)',
                color: STAGE_TONE[r.stage] || 'var(--color-text)'
              }}>
                {r.total.toLocaleString()}
              </div>
            </div>
          ))}
        </div>
      )}
    </ReportCard>
  );
}

// Site Churn by Month -- sites that flipped to Inactive per month.
// SFTP Coverage by Site (Dave 2026-06-12: "let's create an SFTP
// report"). Per-site picture of host/user/file-types/last-pulled +
// flags for which sites are missing local credentials or aren't
// linked to xsiteio. Table view shows every site; chart view rolls
// up coverage % per fuel brand. Local filter for include/exclude
// inactive (defaults to Active-only).
function SftpCoverageCard({ display = 'table' }) {
  const [includeInactive, setIncludeInactive] = useState(false);
  const [query, setQuery] = useState('');
  const { rows, loading, error } = useReport('/api/reports/sftp-coverage', {
    include_inactive: includeInactive ? 1 : undefined
  });

  // Free-text filter across company / site / username / host.
  const filtered = useMemo(() => {
    if (!rows) return [];
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const hay = `${r.company || ''} ${r.site_number || ''} ${r.site_name || ''} ${r.username || ''} ${r.host || ''} ${r.fuel_brand || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [rows, query]);

  // Roll up coverage by fuel brand for the chart variant.
  const byBrand = useMemo(() => {
    if (!rows) return [];
    const map = new Map();
    for (const r of rows) {
      const k = r.fuel_brand || '(none)';
      if (!map.has(k)) map.set(k, { fuel_brand: k, with_sftp: 0, without_sftp: 0 });
      const slot = map.get(k);
      if (r.has_credential) slot.with_sftp += 1;
      else                  slot.without_sftp += 1;
    }
    return Array.from(map.values()).sort((a, b) => (b.with_sftp + b.without_sftp) - (a.with_sftp + a.without_sftp));
  }, [rows]);

  // Top-line counts for the subtitle.
  const totals = useMemo(() => {
    if (!rows) return { sites: 0, with_sftp: 0, without_sftp: 0, no_xsiteio: 0 };
    return rows.reduce((a, r) => {
      a.sites += 1;
      if (r.has_credential)    a.with_sftp += 1; else a.without_sftp += 1;
      if (!r.has_xsiteio_link) a.no_xsiteio += 1;
      return a;
    }, { sites: 0, with_sftp: 0, without_sftp: 0, no_xsiteio: 0 });
  }, [rows]);

  function exportCsv() {
    if (!filtered || filtered.length === 0) return;
    const cols = ['company','site_number','site_name','fuel_brand','status','host','port','username','password','file_types','source_dir','destination_dir','run_time_minutes','date_received','has_credential','has_xsiteio_link'];
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const lines = [cols.join(',')].concat(filtered.map((r) => cols.map((c) => esc(r[c])).join(',')));
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sftp-coverage-${new Date().toISOString().slice(0,10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  return (
    <ReportCard
      title="SFTP Coverage by Site"
      subtitle={
        rows
          ? `${totals.sites.toLocaleString()} sites · ${totals.with_sftp.toLocaleString()} with SFTP · ${totals.without_sftp.toLocaleString()} without · ${totals.no_xsiteio.toLocaleString()} not linked to xsiteio`
          : 'Per-site SFTP details — host, username, file types, last pulled.'
      }
      headerRight={
        <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            type="search"
            placeholder="Search company / site / username…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            style={{ height: 'var(--input-h-sm)', fontSize: 'var(--text-small)', minWidth: 240 }}
          />
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 'var(--text-small)', color: 'var(--color-text-dim)' }}>
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
            />
            Include inactive
          </label>
          <button type="button" className="btn-secondary btn-sm" onClick={exportCsv} disabled={!filtered || filtered.length === 0}>
            Export CSV
          </button>
        </div>
      }
      loading={loading}
      error={error}
      empty={rows && rows.length === 0 ? 'No sites match the current filters.' : null}
    >
      {/* Chart variant: stacked bar of with/without SFTP per fuel brand. */}
      {rows && rows.length > 0 && display === 'chart' && (
        <ResponsiveContainer width="100%" height={Math.max(180, byBrand.length * 32)}>
          <BarChart data={byBrand} layout="vertical" margin={{ top: 8, right: 24, bottom: 8, left: 24 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis type="number" allowDecimals={false} stroke="var(--color-text-dim)" />
            <YAxis type="category" dataKey="fuel_brand" stroke="var(--color-text-dim)" width={120} />
            <Tooltip />
            <Legend />
            <Bar dataKey="with_sftp"    name="With SFTP"    stackId="a" fill={CHART_COLORS.good} />
            <Bar dataKey="without_sftp" name="Missing SFTP" stackId="a" fill={CHART_COLORS.bad} />
          </BarChart>
        </ResponsiveContainer>
      )}

      {/* Table variant: full per-site list. Capped at ~500 visible rows
          to keep the page light -- export CSV for the rest. */}
      {rows && rows.length > 0 && display === 'table' && filtered.length === 0 && (
        <p className="panel-empty">No sites match the search.</p>
      )}
      {rows && rows.length > 0 && display === 'table' && filtered.length > 0 && (
        <>
          <table className="data-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>Customer</th>
                <th style={{ textAlign: 'left' }}>Site</th>
                <th style={{ textAlign: 'left' }}>Brand</th>
                <th style={{ textAlign: 'left' }}>Host</th>
                <th style={{ textAlign: 'left' }}>Username</th>
                <th style={{ textAlign: 'left' }}>Password</th>
                <th style={{ textAlign: 'left' }}>File Types</th>
                <th style={{ textAlign: 'left' }}>Last Pulled</th>
                <th style={{ textAlign: 'center' }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.slice(0, 500).map((r) => {
                const missingSftp     = !r.has_credential;
                const missingXsiteio  = !r.has_xsiteio_link;
                return (
                  <tr key={r.site_id}>
                    <td>{r.company}</td>
                    <td>
                      <div className="cell-mono" style={{ fontWeight: 'var(--font-semibold)' }}>
                        {r.site_number || '(no #)'}
                      </div>
                      {r.site_name && (
                        <div style={{ fontSize: 'var(--text-small)', color: 'var(--color-text-dim)' }}>
                          {r.site_name}
                        </div>
                      )}
                    </td>
                    <td>{r.fuel_brand || '—'}</td>
                    <td className="cell-mono">{r.host || <span style={{ color: 'var(--color-text-faint)' }}>—</span>}</td>
                    <td className="cell-mono">{r.username || <span style={{ color: 'var(--color-text-faint)' }}>—</span>}</td>
                    <td className="cell-mono">{r.password || <span style={{ color: 'var(--color-text-faint)' }}>—</span>}</td>
                    <td className="cell-mono">{r.file_types || <span style={{ color: 'var(--color-text-faint)' }}>—</span>}</td>
                    <td className="cell-mono">{r.date_received ? String(r.date_received).slice(0, 10) : <span style={{ color: 'var(--color-text-faint)' }}>never</span>}</td>
                    <td style={{ textAlign: 'center' }}>
                      {missingSftp && (
                        <span title="No SFTP credential stored locally" style={{
                          fontSize: 'var(--text-caption)', fontWeight: 'var(--font-semibold)',
                          color: 'var(--color-bad)', background: 'var(--color-bad-bg)',
                          padding: '2px 6px', borderRadius: 'var(--radius-sm)', marginRight: 4
                        }}>MISSING</span>
                      )}
                      {missingXsiteio && (
                        <span title="Not linked to xsiteio (no xsiteio_site_id)" style={{
                          fontSize: 'var(--text-caption)', fontWeight: 'var(--font-semibold)',
                          color: 'var(--color-warn)', background: 'var(--color-warn-bg)',
                          padding: '2px 6px', borderRadius: 'var(--radius-sm)'
                        }}>NO XSITEIO</span>
                      )}
                      {!missingSftp && !missingXsiteio && (
                        <span title="SFTP credentials in Command + xsiteio link stamped" style={{
                          fontSize: 'var(--text-caption)', fontWeight: 'var(--font-semibold)',
                          color: 'var(--color-good)', background: 'var(--color-good-bg)',
                          padding: '2px 6px', borderRadius: 'var(--radius-sm)'
                        }}>OK</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {filtered.length > 500 && (
            <p style={{ marginTop: 'var(--space-3)', fontSize: 'var(--text-small)', color: 'var(--color-text-dim)' }}>
              Showing first 500 of {filtered.length.toLocaleString()} rows. Use Export CSV for the full set.
            </p>
          )}
        </>
      )}
    </ReportCard>
  );
}

function SiteChurnByMonthCard({ range, display = 'table' }) {
  const { rows, loading, error } = useReport('/api/reports/site-churn-by-month', {
    from: range.from || undefined,
    to:   range.to   || undefined
  });
  const chartRows = useMemo(() => (rows ? [...rows].reverse() : []), [rows]);
  return (
    <ReportCard
      title="Site Churn by Month"
      subtitle="Sites flipped from Active to Inactive per calendar month, anchored on status_changed_at."
      loading={loading}
      error={error}
      empty={rows && rows.length === 0 ? 'No sites went inactive in this range.' : null}
    >
      {rows && rows.length > 0 && display === 'chart' && (
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={chartRows} margin={{ top: 8, right: 24, bottom: 8, left: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis dataKey="month" stroke="var(--color-text-dim)" />
            <YAxis allowDecimals={false} stroke="var(--color-text-dim)" />
            <Tooltip />
            <Bar dataKey="sites_lost" name="Sites lost" fill={CHART_COLORS.bad} />
          </BarChart>
        </ResponsiveContainer>
      )}
      {rows && rows.length > 0 && display === 'table' && (
        <table className="data-table" style={{ width: '100%' }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}>Month</th>
              <th style={{ textAlign: 'right' }}>Sites Lost</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.month}>
                <td className="cell-mono">{r.month}</td>
                <td className="cell-mono" style={{ textAlign: 'right', fontWeight: 'var(--font-semibold)', color: r.sites_lost > 0 ? 'var(--color-bad)' : 'var(--color-text-dim)' }}>
                  {r.sites_lost.toLocaleString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </ReportCard>
  );
}

// Emails Sent by User -- per-user totals across all document types
// (Email / Quote / Bulloch Request), with the document-type breakdown
// inline so Dave can see which channel each user worked.
function EmailsByUserCard({ range, display = 'table' }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('all'); // 'all' | 'Email' | 'Quote' | 'Bulloch Request'

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const params = {};
    if (range.from)               params.from = range.from;
    if (range.to)                 params.to   = range.to;
    if (typeFilter !== 'all')     params.type = typeFilter;
    axios.get('/api/reports/emails-by-user', { params })
      .then((r) => { if (!cancelled) setRows(r.data.rows || []); })
      .catch((e) => { if (!cancelled) setError(e.response?.data?.error || e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [range.from, range.to, typeFilter]);

  const totalSent  = rows ? rows.reduce((s, r) => s + Number(r.total  || 0), 0) : 0;
  const totalOpens = rows ? rows.reduce((s, r) => s + Number(r.opened || 0), 0) : 0;

  return (
    <div className="card" style={{ marginTop: 'var(--space-4)', padding: 'var(--card-pad)' }}>
      <header style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 'var(--space-3)',
        marginBottom: 'var(--space-3)',
        flexWrap: 'wrap'
      }}>
        <div>
          <h3 style={{ margin: 0 }}>Emails Sent by User</h3>
          <p style={{ margin: '4px 0 0', color: 'var(--color-text-dim)', fontSize: 'var(--text-small)' }}>
            Per-user totals across Email, Quote, and Bulloch Back-Office sends.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 'var(--space-1)', alignItems: 'center' }}>
          <label style={{ fontSize: 'var(--text-small)', color: 'var(--color-text-dim)' }}>Type:</label>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            style={{ height: 'var(--input-h-sm)', fontSize: 'var(--text-small)' }}
          >
            <option value="all">All</option>
            <option value="Email">Email only</option>
            <option value="Quote">Quote only</option>
            <option value="Bulloch Request">Bulloch only</option>
          </select>
        </div>
      </header>

      {loading && <p>Loading…</p>}
      {error && <p className="error">Error: {error}</p>}
      {!loading && !error && rows && rows.length === 0 && (
        <p className="panel-empty">No sends in this range.</p>
      )}
      {!loading && !error && rows && rows.length > 0 && display === 'table' && (
        <table className="data-table" style={{ width: '100%' }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}>User</th>
              <th style={{ textAlign: 'right' }}>Total</th>
              <th style={{ textAlign: 'right' }}>Emails</th>
              <th style={{ textAlign: 'right' }}>Quotes</th>
              <th style={{ textAlign: 'right' }}>Bulloch</th>
              <th style={{ textAlign: 'right' }}>Opened</th>
              <th style={{ textAlign: 'right' }}>Open Rate</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.user_email || r.user_name}>
                <td>
                  <div style={{ fontWeight: 'var(--font-semibold)' }}>{r.user_name}</div>
                  {r.user_email && (
                    <div className="cell-mono" style={{ color: 'var(--color-text-dim)', fontSize: 'var(--text-small)' }}>
                      {r.user_email}
                    </div>
                  )}
                </td>
                <td className="cell-mono" style={{ textAlign: 'right', fontWeight: 'var(--font-semibold)' }}>
                  {r.total.toLocaleString()}
                </td>
                <td className="cell-mono" style={{ textAlign: 'right' }}>{r.emails.toLocaleString()}</td>
                <td className="cell-mono" style={{ textAlign: 'right' }}>{r.quotes.toLocaleString()}</td>
                <td className="cell-mono" style={{ textAlign: 'right' }}>{r.bulloch_requests.toLocaleString()}</td>
                <td className="cell-mono" style={{ textAlign: 'right' }}>{r.opened.toLocaleString()}</td>
                <td className="cell-mono" style={{ textAlign: 'right' }}>{r.open_rate}%</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td style={{ fontWeight: 'var(--font-semibold)', color: 'var(--color-text-dim)' }}>Total</td>
              <td className="cell-mono" style={{ textAlign: 'right', fontWeight: 'var(--font-semibold)' }}>
                {totalSent.toLocaleString()}
              </td>
              <td colSpan={3} />
              <td className="cell-mono" style={{ textAlign: 'right', fontWeight: 'var(--font-semibold)' }}>
                {totalOpens.toLocaleString()}
              </td>
              <td className="cell-mono" style={{ textAlign: 'right', fontWeight: 'var(--font-semibold)' }}>
                {totalSent > 0 ? Math.round((totalOpens / totalSent) * 100) : 0}%
              </td>
            </tr>
          </tfoot>
        </table>
      )}
      {!loading && !error && rows && rows.length > 0 && display === 'chart' && (
        <ResponsiveContainer width="100%" height={Math.max(120, rows.length * 50)}>
          <BarChart data={rows} layout="vertical" margin={{ top: 8, right: 24, bottom: 8, left: 24 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis type="number" stroke="var(--color-text-dim)" />
            <YAxis type="category" dataKey="user_name" stroke="var(--color-text-dim)" width={140} />
            <Tooltip />
            <Legend />
            <Bar dataKey="emails"           name="Emails"  stackId="a" fill={CHART_COLORS.email} />
            <Bar dataKey="quotes"           name="Quotes"  stackId="a" fill={CHART_COLORS.quote} />
            <Bar dataKey="bulloch_requests" name="Bulloch" stackId="a" fill={CHART_COLORS.bulloch} />
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}
